import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Trash2, ArrowLeft, CreditCard, Truck, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useShop } from '../contexts/ShopContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, removeFromCart, updateQuantity, clearCart } = useShop();
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState('cart'); // 'cart' or 'checkout'
  const [shipping, setShipping] = useState({
    full_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US'
  });

  // Calculate totals
  const shippingCost = cartTotal >= 50 ? 0 : 5.99;
  const total = cartTotal + shippingCost;

  const handleCheckout = async () => {
    // Validate shipping info
    if (!shipping.full_name || !shipping.email || !shipping.address_line1 || 
        !shipping.city || !shipping.state || !shipping.zip_code) {
      toast.error('Please fill in all required shipping fields');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/shop/checkout`, {
        cart: cart.map(item => ({
          product_id: item.product_id,
          size: item.size,
          color: item.color,
          quantity: item.quantity
        })),
        shipping,
        origin_url: window.location.origin
      });

      // Redirect to Stripe Checkout.
      // NOTE: do NOT clearCart() here — if the customer cancels the Stripe
      // page and returns, we want their cart preserved so they can retry.
      // The cart is cleared on OrderSuccess ONLY after payment_status=paid.
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setProcessing(false);
    }
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  if (cart.length === 0) {
    return (
      <div className="min-h-screen py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <ShoppingBag className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Your cart is empty</h1>
          <p className="text-zinc-400 mb-6">Looks like you haven't added any items yet.</p>
          <Link to="/shop">
            <Button className="bg-[#D9F99D] text-black hover:bg-[#BEF264]">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-16">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/shop">
            <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold">
            {step === 'cart' ? 'Shopping Cart' : 'Checkout'}
          </h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items / Checkout Form */}
          <div className="lg:col-span-2 space-y-6">
            {step === 'cart' ? (
              <>
                {/* Cart Items */}
                {cart.map((item, idx) => (
                  <div key={`${item.product_id}-${item.size}-${item.color}-${idx}`} className="glass-card rounded-2xl p-4 md:p-6">
                    <div className="flex gap-4">
                      {/* Image */}
                      <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden bg-zinc-900 flex-shrink-0">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ShoppingBag className="w-8 h-8 text-zinc-700" />
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg">{item.name}</h3>
                        <p className="text-zinc-400 text-sm">{item.size} / {item.color}</p>
                        <p className="text-[#D9F99D] font-bold text-lg mt-1">${item.price.toFixed(2)}</p>

                        {/* Quantity Controls */}
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center gap-3">
                            <Label className="text-zinc-400 text-sm">Qty:</Label>
                            <select
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.product_id, item.size, item.color, parseInt(e.target.value))}
                              className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm w-20"
                            >
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                          </div>
                          <Button
                            variant="ghost"
                            onClick={() => removeFromCart(item.product_id, item.size, item.color)}
                            className="text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Continue to Checkout */}
                <Button
                  onClick={() => setStep('checkout')}
                  className="w-full bg-[#D9F99D] text-black hover:bg-[#BEF264] py-6 rounded-xl font-bold"
                  data-testid="continue-checkout-btn"
                >
                  Continue to Checkout
                </Button>
              </>
            ) : (
              /* Checkout Form */
              <div className="glass-card rounded-2xl p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#D9F99D]" />
                  Shipping Information
                </h2>

                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        value={shipping.full_name}
                        onChange={(e) => setShipping({ ...shipping, full_name: e.target.value })}
                        placeholder="John Doe"
                        className="bg-black/20 border-white/10"
                        data-testid="shipping-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={shipping.email}
                        onChange={(e) => setShipping({ ...shipping, email: e.target.value })}
                        placeholder="john@example.com"
                        className="bg-black/20 border-white/10"
                        data-testid="shipping-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Phone (optional)</Label>
                    <Input
                      type="tel"
                      value={shipping.phone}
                      onChange={(e) => setShipping({ ...shipping, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="bg-black/20 border-white/10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Address Line 1 *</Label>
                    <Input
                      value={shipping.address_line1}
                      onChange={(e) => setShipping({ ...shipping, address_line1: e.target.value })}
                      placeholder="123 Main Street"
                      className="bg-black/20 border-white/10"
                      data-testid="shipping-address"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Address Line 2</Label>
                    <Input
                      value={shipping.address_line2}
                      onChange={(e) => setShipping({ ...shipping, address_line2: e.target.value })}
                      placeholder="Apt 4B"
                      className="bg-black/20 border-white/10"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>City *</Label>
                      <Input
                        value={shipping.city}
                        onChange={(e) => setShipping({ ...shipping, city: e.target.value })}
                        placeholder="New York"
                        className="bg-black/20 border-white/10"
                        data-testid="shipping-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State *</Label>
                      <select
                        value={shipping.state}
                        onChange={(e) => setShipping({ ...shipping, state: e.target.value })}
                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                        data-testid="shipping-state"
                      >
                        <option value="">Select State</option>
                        {states.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>ZIP Code *</Label>
                      <Input
                        value={shipping.zip_code}
                        onChange={(e) => setShipping({ ...shipping, zip_code: e.target.value })}
                        placeholder="10001"
                        className="bg-black/20 border-white/10"
                        data-testid="shipping-zip"
                      />
                    </div>
                  </div>

                  {/* Back Button */}
                  <Button
                    variant="outline"
                    onClick={() => setStep('cart')}
                    className="mt-4 bg-white/5 border-white/10"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Cart
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="glass-card rounded-2xl p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-6">Order Summary</h2>

              {/* Items Summary */}
              <div className="space-y-3 mb-6">
                {cart.map((item, idx) => (
                  <div key={`summary-${item.product_id}-${idx}`} className="flex justify-between text-sm">
                    <span className="text-zinc-400 truncate mr-2">
                      {item.name} x {item.quantity}
                    </span>
                    <span>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Subtotal</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Shipping</span>
                  {shippingCost === 0 ? (
                    <span className="text-green-400">FREE</span>
                  ) : (
                    <span>${shippingCost.toFixed(2)}</span>
                  )}
                </div>
                {cartTotal < 50 && (
                  <p className="text-xs text-zinc-500">Free shipping on orders over $50</p>
                )}
                <div className="flex justify-between font-bold text-lg pt-3 border-t border-white/10">
                  <span>Total</span>
                  <span className="text-[#D9F99D]">${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Checkout Button (only on checkout step) */}
              {step === 'checkout' && (
                <Button
                  onClick={handleCheckout}
                  disabled={processing}
                  className="w-full bg-[#D9F99D] text-black hover:bg-[#BEF264] py-6 rounded-xl font-bold mt-6"
                  data-testid="pay-now-btn"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  {processing ? 'Processing...' : `Pay $${total.toFixed(2)}`}
                </Button>
              )}

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                <div className="flex items-center gap-3 text-sm text-zinc-400">
                  <Shield className="w-4 h-4 text-green-400" />
                  <span>Secure checkout powered by Stripe</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-zinc-400">
                  <Truck className="w-4 h-4 text-blue-400" />
                  <span>Fast shipping (3-5 business days)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
